import { Helmet } from "react-helmet"
import { Link } from "react-router-dom"
import React, { useEffect, useState } from "react"

import Config from "../../api/Config"
import { ASSETS } from "../../api/Registry"
import { CreateCategory } from "./CreateCategory"
import HttpServices from "../../services/HttpServices"
import { LoadingXL } from "../../components/utils/LoadingXL"
import { ModuleBreadCrumb } from "../../lib/fragments/ModuleBreadCrumb"
import { usePageContext } from "../../lib/guards/PageContext"
import { ErrorState } from "../errors/ErrorState"

export const AssetConfiguration = () => {
    const [state, setstate] = useState({
        reload: false,
        httpStatus: 200,
        status: 'pending',
        data: {
            categories: null,
        },
        show: {
            createType: false,
            createCategory: false,
        }
    })

    const pageTitle = "ASMX Categorization"
    const { updateDataContext } = usePageContext()
    const [newCategory, setNewCategory] = React.useState(null)

    const breadCrumb = [
        { linkItem: false, title: 'Services' },
        { linkItem: false, title: pageTitle },
    ]

    const fetchAssetCategories = async () => {
        let { status } = state
        let { httpStatus } = state

        let categories = null

        try {
            const response: any = await HttpServices.httpGet(ASSETS.ALL_CATEGORIES)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload

                status = 'fulfilled'
                categories = payload.categories

                updateDataContext({
                    title: pageTitle,
                    mainAction: {
                        visible: true,
                        disabled: false,
                        icon: 'far fa-plus',
                        label: 'Create Category',
                        onClick: toggleCreateCategoryPanel,
                        variant: 'primary' /* | 'secondary' | 'danger' | string */,
                    }
                });
            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
        }

        setstate((prevState) => ({
            ...prevState,
            status,
            httpStatus,
            reload: false,
            data: {
                ...prevState.data,
                categories: categories,
            },
        }));
    }

    useEffect(() => {
        updateDataContext({
            title: pageTitle
        })

        fetchAssetCategories()
    }, [])

    const toggleCreateCategoryPanel = () => {
        setstate((prev) => ({
            ...prev,
            show: {
                ...prev.show,
                createCategory: !prev.show.createCategory
            }
        }))
    }

    const onCreateCategoryComplete = (actionData: any) => {
        toggleCreateCategoryPanel()
        setNewCategory(actionData)
    }

    const reloadTable = () => {
        setstate((prevState) => ({
            ...prevState, reload: !prevState.reload
        }));

        fetchAssetCategories()
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle} - {Config.APP_NAME}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                <div className="w-full py-2 border-b border-slate-200 dark:border-slate-600 lg:px-6 px-5 flex flex-col">
                    <div className="flex-none">
                        <ModuleBreadCrumb breadCrumb={breadCrumb} />
                    </div>
                </div>

                {
                    state.status === 'rejected' ? (
                        <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                            <ErrorState
                                code={state.httpStatus}
                            />
                        </div>
                    ) : state.status === 'fulfilled' ? (
                        <>
                            <div className="header-title align-middle title-transition lg:px-6 px-5 pt-3">
                                <div className="md:hidden flex items-center align-middle pb-3">
                                    <div className="flex-1">
                                        <h3 className="text-slate-900 dark:text-white text-lg md:font-normal font-medium tracking-wide">
                                            {pageTitle}
                                        </h3>
                                    </div>

                                    <div className="flex-none flex flex-row-reverse">
                                        <button type="button" onClick={toggleCreateCategoryPanel} className={`w-auto min-w-24 disabled:cursor-not-allowed text-sm rounded-md border border-transparent px-2 py-1.5 bg-inherit hover:bg-orange-50 text-orange-600 dark:hover:bg-orange-900/40 focus:outline-none flex items-center align-middle gap-x-2`}>
                                            <span className="far fa-plus"></span>

                                            <span className="text-sm">
                                                Create Category
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <p className="text-slate-600 dark:text-slate-300 md:pt-2 lg:pt-2 pb-4 text-sm md:w-[512px]">
                                    <span className="hidden md:inline-block">
                                        Manage and customize asset attributes, categories, and types to ensure all assets are accurately represented and tailored to your organization's needs.
                                    </span>

                                    <span className="md:hidden">
                                        Customize and manage attributes for different asset types
                                    </span>
                                </p>

                                <ul className="w-full relative pb-20">
                                    {
                                        state.reload ? (
                                            <div className="absolute inset-0 bg-white dark:bg-slate-800 dark:bg-opacity-70 bg-opacity-70 flex items-center justify-center">
                                                <span className="fa-duotone text-theme fa-spinner-third fa-2x m-auto block fa-spin"></span>
                                            </div>
                                        ) : null
                                    }

                                    {
                                        newCategory && (
                                            <li key={newCategory.uuid} className="bg-inherit hover:bg-gray-100 md:px-2 dark:hover:bg-gray-900 border-b border-gray-300 dark:border-gray-600 text-sm">
                                                <div className="w-full flex flex-row items-center align-middle gap-x-2">
                                                    <div className="flex-grow">
                                                        <h3 className="text-slate-900 dark:text-white text-base font-medium tracking-tight">
                                                            {newCategory.name}
                                                        </h3>
                                                    </div>

                                                    <div className="flex-none py-3">
                                                        <Link to={newCategory.uuid} className="text-sm text-theme dark:text-theme-secondary">
                                                            Show Details
                                                        </Link>
                                                    </div>
                                                </div>

                                                <div className="w-full md:block hidden pb-1.5">
                                                    <p className="text-sm block">
                                                        {newCategory.description}
                                                    </p>
                                                </div>
                                            </li>
                                        )
                                    }

                                    {
                                        state.data.categories.map((category: any) => {
                                            return (
                                                <>
                                                    <li key={category.uuid} className="bg-inherit hover:bg-gray-100 md:px-2 dark:hover:bg-gray-900 border-b border-gray-300 dark:border-gray-600 text-sm">
                                                        <div className="w-full flex flex-row items-center align-middle gap-x-2 py-2">
                                                            <div className="flex-grow">
                                                                <h3 className="text-slate-900 dark:text-white text-base font-medium tracking-tight">
                                                                    {category.name}
                                                                </h3>
                                                            </div>

                                                            <div className="flex-none">
                                                                <Link to={category.uuid} className="text-sm text-theme dark:text-theme-secondary">
                                                                    Show Details
                                                                </Link>
                                                            </div>
                                                        </div>

                                                        <div className="w-full md:block hidden pb-1.5">
                                                            <p className="text-sm block">
                                                                {category.description}
                                                            </p>
                                                        </div>
                                                    </li>
                                                </>
                                            )
                                        })
                                    }
                                </ul>
                            </div>
                        </>
                    ) : (
                        <div className="flex-grow w-full overflow-auto h-full flex flex-row py-4 justify-center align-middle items-center">
                            <LoadingXL />
                        </div>
                    )
                }
            </div>

            <CreateCategory
                show={state.show.createCategory}
                onComplete={onCreateCategoryComplete}
                showOrHide={toggleCreateCategoryPanel}
            />
        </React.Fragment>
    )
}